import React, { useState, useEffect, useRef } from "react";
import { FaPlus, FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import AddGroupModal from "./addGroup";
import {
  addGroup,
  deleteGroups,
  updateGroup,
} from "../../../../services/commonapi/commonApi";
import moment from "moment";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchGroups,
  resetGroupsState,
} from "../../../../redux/slices/groupsSlice";
import { Pagination } from "../../../../components/Stepper/fields/inputField";
import { useNavigate } from "react-router-dom";
import { handleErrorRedirect } from "../../../../utility/errorRedirect";
import Breadcrumb from "../../../../components/Breadcrumb";
import { getBreadcrumbLabelsByAssetType } from "../../../../utility/helper";

const ViewGroups = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [type, setType] = useState("Create");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({});
  const dispatch = useDispatch();
  const toast = useRef(null);
  const accessToken = JSON.parse(localStorage.getItem("access_token"));
  const { groupsData, groupLoading } = useSelector((state) => state.groups);
  const navigate = useNavigate();
  const breadcrumbItems = getBreadcrumbLabelsByAssetType("Groups");

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchGroups(accessToken)).then((resultAction) => {
        if (fetchGroups.rejected.match(resultAction)) {
          const status = resultAction?.payload?.status;
          handleErrorRedirect(status, navigate);
        }
      });
    }

    return () => {
      dispatch(resetGroupsState());
    };
  }, [dispatch, accessToken, navigate]);

  const openConfirmDialog = (config) => {
    setDialogConfig(config);
    setIsDialogOpen(true);
  };

  const confirmDelete = (user) => {
    openConfirmDialog({
      title: "Confirm Delete",
      message: `Are you sure you want to delete this group?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      cancelClass:
        "ml-6 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium border dark:border-gray-300 border-info rounded-md bg-transparent text-info dark:text-grey rounded-md focus:outline-none",
      confirmClass:
        "ml-6 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium dark:text-black dark:button-primary-gradient rounded-md bg-lightPrimary text-white rounded-md focus:outline-none",
      onConfirm: () => handleDelete(user),
    });
  };

  const handleDelete = async (user) => {
    try {
      const accessToken = JSON.parse(localStorage.getItem("access_token"));
      const response = await deleteGroups(accessToken, user?.id);
      if (response) {
        dispatch(fetchGroups(accessToken));
        toast.current.show({
          className: "bg-green-300 text-white",
          severity: "success",
          summary: "Success",
          detail: "Group deleted successfully",
          life: 3000,
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      handleErrorRedirect(status, navigate);
      toast.current.show({
        className: "bg-yellow-700 text-white",
        severity: "warn",
        summary: "Warning",
        detail: "Failed to delete group",
        life: 3000,
      });
    }
  };

  const dateTemplate = (rowData) => {
    return rowData.created_at
      ? moment(rowData.created_at).format("DD-MM-YYYY")
      : "no data";
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <button
          disabled={rowData?.is_default}
          className={`focus:outline-none ${
            rowData?.is_default
              ? "dark:text-gray-400 text-info cursor-not-allowed"
              : "text-primary"
          }`}
        >
          <FaRegEdit
            onClick={() => {
              if (!rowData?.is_default) {
                setIsModalOpen(true);
                setType("edit");
                setSelectedGroup(rowData);
              }
            }}
          />
        </button>
        <button
          disabled={rowData?.is_default}
          className={`focus:outline-none ${
            rowData?.is_default
              ? "dark:text-gray-400 text-info cursor-not-allowed"
              : "text-darkWaring"
          }`}
        >
          <FaRegTrashAlt
            onClick={() => {
              if (!rowData?.is_default) {
                confirmDelete(rowData);
              }
            }}
          />
        </button>
      </div>
    );
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const handleAddGroupClick = () => {
    setType("Create");
    setIsModalOpen(true);
  };

  const handleSubmit = async ({
    name,
    status,
    onClose,
    setError,
    setLoading,
  }) => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    setLoading(true);
    try {
      const response =
        type === "Create"
          ? await addGroup(name, status, accessToken)
          : await updateGroup(name, status, selectedGroup.id, accessToken);

      if (response.data.status === true) {
        dispatch(fetchGroups(accessToken));
        toast.current.show({
          className: "bg-green-300 text-white",
          severity: "success",
          summary: "Success",
          detail: response.data.message,
          life: 3000,
        });

        onClose();
      } else {
        toast.current.show({
          className: "bg-yellow-700 text-white",
          severity: "warn",
          summary: "Warning",
          detail: response.data.message || "Failed to add group",
          life: 3000,
        });
      }
    } catch (error) {
      const status = error?.response?.status;
      handleErrorRedirect(status, navigate);
      console.log("Error adding group:", error);
      toast.current.show({
        className: "bg-red-700 text-white",
        severity: "error",
        summary: "Error",
        detail: error.message || "An unexpected error occurred",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        {...dialogConfig}
      />
      <Breadcrumb items={breadcrumbItems} className="md:ml-6 mb-7" />
      <div className="p-4">
        <div className="flex justify-between items-center mb-4 md:mb-7">
          <h2 className="text-2xl md:ml-4 text-lightPrimary dark:text-primary-gradient text-center font-semibold">
            View Groups
          </h2>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-1 text-sm font-medium px-4 py-2 ml-6 rounded-md border transition-all focus:outline-none text-lightPrimary border-lightPrimary bg-[radial-gradient(circle_at_10%_10%,rgb(237,237,237),#9e9e9e,#9e9e9e)] dark:text-black dark:button-primary-gradient"
            onClick={handleAddGroupClick}
          >
            <FaPlus /> Add Group
          </button>
        </div>

        <DataTable
          value={groupsData}
          stripedRows
          className="userTable"
          // paginator
          rows={limit}
          totalRecords={groupsData.length}
          first={page * limit}
          onPage={(e) => {
            setPage(e.page);
            setLimit(e.rows);
          }}
          loading={loading || groupLoading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            field="name"
            header="Name"
            sortable
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-grey font-light"
          />
          <Column
            field="status"
            header="Status"
            align={"right"}
            body={(data) => (data.status === true ? "Active" : "Inactive")}
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-grey font-light"
          />
          <Column
            field="created_at"
            header="Created Date"
            align={"right"}
            body={dateTemplate}
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-grey font-light"
          />
          <Column
            field="Action"
            header="Action"
            align={"right"}
            body={actionBodyTemplate}
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-primary dark:text-grey font-light flex justify-end"
          />
        </DataTable>
        <Pagination
          page={page}
          limit={limit}
          total={groupsData?.total_records || 0}
          onPageChange={(newPage) => setPage(newPage)}
        />
        {isModalOpen && (
          <AddGroupModal
            type={type}
            selectedGroup={selectedGroup}
            onClose={handleModalClose}
            handleSubmit={handleSubmit}
          />
        )}
      </div>
    </>
  );
};

export default ViewGroups;
